// src/views/adminView/contractsView/ContractRenewalSection.jsx
// Sección "Contrato firmado y vigencia" de la pestaña Contrato del detalle de empresa: firma,
// vigencia y estado del contrato, la acción "Renovar contrato" (solo admin y superadmin
// logístico: la decide el padre con `canRenew`) y el historial de contratos firmados con la
// descarga del PDF. El modal de renovación vive en el padre: también lo abre la fila
// "Contrato marco" del cumplimiento.
import { useTranslation } from "react-i18next";
import { FileSignature, RefreshCw } from "lucide-react";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { SectionCard } from "../../../components/ui/SectionCard";
import SignedContractsList from "./SignedContractsList";
import { formatContractDay, renovacionUrgente, vigenciaDe } from "./contractRenewal";

// Rótulos (claves i18n) y tonos: los mismos que usan el Directorio y DetalleKpis.
const VIGENCIA = {
  terminada: { label: "Terminada", tone: "error" },
  por_vencer: { label: "Por vencer", tone: "warning" },
  vigente: { label: "Vigente", tone: "success" },
  indefinida: { label: "Indefinida", tone: "neutral" },
};

const STATUS = {
  active: { label: "Activo", tone: "success" },
  inactive: { label: "Inactivo", tone: "neutral" },
  pending: { label: "Pendiente", tone: "warning" },
};

const TERM_LABEL = "text-[11px] font-semibold uppercase tracking-wider text-fg-muted";

/**
 * @param {{ contract: object, canRenew: boolean, onRenew: () => void }} props
 */
export default function ContractRenewalSection({ contract, canRenew, onRenew }) {
  const { t } = useTranslation();
  const vigencia = VIGENCIA[vigenciaDe(contract)];
  const status = STATUS[contract?.status] ?? STATUS.inactive;
  const signedOn = formatContractDay(contract?.dateSigned);
  const from = formatContractDay(contract?.validityPeriod?.startDate ?? contract?.dateSigned);
  const to = formatContractDay(contract?.validityPeriod?.endDate);

  return (
    <SectionCard
      tone="bg"
      icon={<FileSignature className="w-4 h-4" aria-hidden="true" />}
      title={t("Contrato firmado y vigencia")}
      subtitle={t("Firma, vigencia y copias firmadas del contrato con el comercio.")}
      // space-y y no mt-5: el `.mt-5` de Bootstrap (global, con !important) lo pisa con 3rem.
      bodyClassName="space-y-5"
      action={
        canRenew ? (
          <Button
            size="sm"
            // Con el contrato terminado o por terminar, renovar es LA acción de la sección.
            variant={renovacionUrgente(contract) ? "primary" : "outline"}
            onClick={onRenew}
            iconLeft={<RefreshCw className="w-4 h-4" aria-hidden="true" />}
          >
            {t("Renovar contrato")}
          </Button>
        ) : null
      }
    >
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <dt className={TERM_LABEL}>{t("Firmado")}</dt>
          <dd className="mt-1 text-sm text-fg tabular-nums">{signedOn || "—"}</dd>
        </div>
        <div>
          <dt className={TERM_LABEL}>{t("Vigencia")}</dt>
          <dd className="mt-1 flex flex-wrap items-center gap-2 text-sm text-fg tabular-nums">
            <span>{to ? `${from || "—"} → ${to}` : t("Desde {{fecha}}", { fecha: from || "—" })}</span>
            <Badge tone={vigencia.tone}>{t(vigencia.label)}</Badge>
          </dd>
        </div>
        <div>
          <dt className={TERM_LABEL}>{t("Estado")}</dt>
          <dd className="mt-1">
            <Badge tone={status.tone} dot>
              {t(status.label)}
            </Badge>
          </dd>
        </div>
      </dl>

      <div>
        <h4 className={TERM_LABEL}>{t("Contratos firmados")}</h4>
        <SignedContractsList
          className="mt-2"
          contractId={contract?._id}
          documents={contract?.signedDocuments}
          emptyHint={canRenew ? t("Al renovar el contrato adjuntas el PDF firmado y queda guardado acá.") : null}
        />
      </div>
    </SectionCard>
  );
}
